import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { money, parseMoneyToCents } from "../lib/format";
import { toast } from "sonner";
import { Plus, X, AlertTriangle } from "lucide-react";

const DCATS = [
  { v: "lab", l: "Laboratory fees" }, { v: "materials", l: "Materials" },
  { v: "other", l: "Other deduction" }, { v: "custom", l: "Custom" },
];

export default function RecordPayment() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: practices } = useQuery({ queryKey: ["practices-min"], queryFn: async () => (await api.get(`/practices`)).data });

  const [practiceId, setPracticeId] = useState("");
  const [form, setForm] = useState({
    period_start: "2026-09-01", period_end: "2026-09-30", report_date: "2026-09-30",
    payment_date: "", total_billing: "", actual: "", notes: "", invoice_reference: "",
  });
  const [deductions, setDeductions] = useState([{ category: "lab", amount: "" }]);
  const [preview, setPreview] = useState(null);
  const [blocker, setBlocker] = useState(null);   // {code, message, versions?}
  const [dupWarn, setDupWarn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [agreementVersionId, setAgreementVersionId] = useState(null);
  const timer = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildBody = (extra = {}) => ({
    practice_id: practiceId,
    period_start: form.period_start, period_end: form.period_end,
    report_date: form.report_date || form.period_end,
    payment_date: form.payment_date || null,
    invoice_reference: form.invoice_reference || null,
    total_billing_cents: parseMoneyToCents(form.total_billing),
    deductions: deductions.filter((d) => d.amount !== "").map((d) => ({ category: d.category, amount_cents: parseMoneyToCents(d.amount) })),
    actual_cents: form.actual === "" ? null : parseMoneyToCents(form.actual),
    agreement_version_id: agreementVersionId,
    ...extra,
  });

  useEffect(() => {
    if (!practiceId || !form.total_billing) { setPreview(null); setBlocker(null); setDupWarn(false); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/payment-reports/preview", buildBody());
        if (data.ok) { setPreview(data.report); setBlocker(null); setDupWarn(data.duplicate_warning); }
        else { setPreview(null); setBlocker(data); setDupWarn(false); }
      } catch (e) { setPreview(null); }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [practiceId, form, deductions, agreementVersionId]);

  const addDed = () => setDeductions((d) => [...d, { category: "other", amount: "" }]);
  const rmDed = (i) => setDeductions((d) => d.filter((_, x) => x !== i));
  const setDed = (i, k, v) => setDeductions((d) => d.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const save = async (confirmDuplicate = false) => {
    if (!practiceId) return toast.error("Please choose a practice.");
    setSaving(true);
    try {
      await api.post("/payment-reports", buildBody({ confirm_duplicate: confirmDuplicate }));
      qc.invalidateQueries();
      toast.success("Payment report saved");
      navigate("/money");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "duplicate") { setDupWarn(true); toast.error("Possible duplicate — review below."); }
      else if (e?.response?.status === 409 && d?.code === "agreement_boundary") { setBlocker(d); toast.error("Agreement changed during this period."); }
      else toast.error(apiError(e));
    }
    setSaving(false);
  };

  const pfee = preview ? (preview.service_fee_cents ?? (preview.adjusted_billing_cents - preview.expected_cents)) : 0;

  return (
    <AppShell>
      <PageHeader title="Record Payment" subtitle="Create a payment report — no work days required" onBack={() => navigate(-1)} />
      <div className="px-5 lg:px-8 pb-44 df-fade-in space-y-4">
        <div className="df-card p-4 space-y-4">
          <div>
            <Label className="text-slate-700">Practice</Label>
            <Select value={practiceId} onValueChange={(v) => { setPracticeId(v); setAgreementVersionId(null); }}>
              <SelectTrigger data-testid="report-practice" className="mt-1.5 h-12 rounded-xl bg-white"><SelectValue placeholder="Choose a practice" /></SelectTrigger>
              <SelectContent>{(practices || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-700">Period start</Label><Input type="date" data-testid="report-period-start" value={form.period_start} onChange={(e) => set("period_start", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" /></div>
            <div><Label className="text-slate-700">Period end</Label><Input type="date" data-testid="report-period-end" value={form.period_end} onChange={(e) => set("period_end", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" /></div>
            <div><Label className="text-slate-700">Report date</Label><Input type="date" data-testid="report-date" value={form.report_date} onChange={(e) => set("report_date", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" /></div>
            <div><Label className="text-slate-700">Payment date (optional)</Label><Input type="date" data-testid="report-payment-date" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" /></div>
          </div>
          <div>
            <Label className="text-slate-700">Total billing</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
              <Input type="number" step="0.01" inputMode="decimal" data-testid="report-total-billing" value={form.total_billing} onChange={(e) => set("total_billing", e.target.value)} placeholder="0.00" className="h-12 rounded-xl bg-white pl-7 font-num" />
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Invoice / reference (optional)</Label>
            <Input data-testid="report-invoice-reference" value={form.invoice_reference} onChange={(e) => set("invoice_reference", e.target.value)} placeholder="e.g. INV-202601" className="mt-1.5 h-12 rounded-xl bg-white" />
            <p className="text-[11px] text-slate-400 mt-1">Shown on your Accountant Pack. Leave blank if this arrangement doesn't use invoice numbers.</p>
          </div>
        </div>

        <div className="df-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">Deductions</div>
            <button data-testid="add-deduction" onClick={addDed} className="text-sm text-[#0D786A] font-medium df-pressable flex items-center gap-1"><Plus size={14} /> Add</button>
          </div>
          {deductions.map((d, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`deduction-row-${i}`}>
              <Select value={d.category} onValueChange={(v) => setDed(i, "category", v)}>
                <SelectTrigger className="h-11 rounded-xl bg-white flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>{DCATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
              <div className="relative w-32">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-num text-sm">$</span>
                <Input type="number" step="0.01" inputMode="decimal" data-testid={`deduction-amount-${i}`} value={d.amount} onChange={(e) => setDed(i, "amount", e.target.value)} placeholder="0.00" className="h-11 rounded-xl bg-white pl-6 font-num text-sm" />
              </div>
              <button onClick={() => rmDed(i)} className="text-slate-400 df-pressable p-1"><X size={16} /></button>
            </div>
          ))}
        </div>

        {blocker && (
          <div className="df-card p-4 border border-amber-200 bg-amber-50" data-testid="report-boundary">
            <div className="flex items-start gap-2 text-sm text-amber-800"><AlertTriangle size={18} className="shrink-0 mt-0.5" /><span>{blocker.message}</span></div>
            {blocker.versions && (
              <div className="mt-3 flex flex-wrap gap-2">
                {blocker.versions.filter(Boolean).map((v) => (
                  <button key={v.id} data-testid={`choose-version-${v.version}`} onClick={() => { setAgreementVersionId(v.id); setBlocker(null); }}
                    className="px-3 h-10 rounded-xl border border-amber-300 bg-white text-sm df-pressable">
                    Use v{v.version} (from {v.effective_start})
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {preview && (
          <div className="df-card p-4 space-y-1.5 text-sm" data-testid="report-review">
            <Row label="Total billing" value={money(preview.total_billing_cents)} />
            <Row label="Less deductions" value={"− " + money(preview.deductions_total_cents)} />
            <Row label="Adjusted billing" value={money(preview.adjusted_billing_cents)} strong />
            <Row label={`Facilities / service fee (${((preview.service_fee_bps || 0) / 100).toFixed(2)}%)`} value={"− " + money(pfee)} />
            {preview.gst_model === "components" ? (
              <>
                {preview.service_fee_gst_cents ? <Row label="GST on facilities fee (10%)" value={"− " + money(preview.service_fee_gst_cents)} /> : null}
                <Row label="Dentist amount" value={money(preview.dentist_net_cents)} strong />
                {preview.dentist_gst_cents ? <Row label="Dentist GST (10%)" value={"+ " + money(preview.dentist_gst_cents)} /> : null}
              </>
            ) : (preview.gst_cents ? <Row label="GST" value={money(preview.gst_cents)} /> : null)}
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-100">
              <span className="font-semibold text-slate-900">Expected payment</span>
              <span className="font-num font-bold text-[#0D786A]" data-testid="report-expected">{money(preview.expected_cents)}</span>
            </div>
          </div>
        )}

        <div className="df-card p-4 space-y-3">
          <div>
            <Label className="text-slate-700">Actual payment received (optional)</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
              <Input type="number" step="0.01" inputMode="decimal" data-testid="report-actual" value={form.actual} onChange={(e) => set("actual", e.target.value)} placeholder="Leave blank if awaiting" className="h-12 rounded-xl bg-white pl-7 font-num" />
            </div>
          </div>
          {preview && form.actual !== "" && (
            <Row label="Variance (actual − expected)" value={money(parseMoneyToCents(form.actual) - preview.expected_cents)} strong />
          )}
          <div><Label className="text-slate-700">Notes (optional)</Label><Textarea data-testid="report-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5 rounded-xl bg-white" /></div>
        </div>

        {dupWarn && (
          <div className="df-card p-4 border border-amber-200 bg-amber-50" data-testid="report-duplicate">
            <div className="flex items-start gap-2 text-sm text-amber-800"><AlertTriangle size={18} className="shrink-0 mt-0.5" /><span>A payment report already exists for this practice and period. Save anyway only if this is a separate report.</span></div>
            <Button data-testid="report-save-anyway" onClick={() => save(true)} disabled={saving} className="mt-3 h-10 rounded-xl bg-amber-600 hover:bg-amber-700">Save anyway</Button>
          </div>
        )}
      </div>

      <div className="df-actionbar">
        <Button variant="outline" onClick={() => navigate(-1)} className="flex-1 h-12 rounded-xl">Cancel</Button>
        <Button data-testid="report-save" onClick={() => save(false)} disabled={saving || !!blocker} className="flex-1 h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
          {saving ? "Saving…" : "Save Payment Report"}
        </Button>
      </div>
    </AppShell>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-slate-600 ${strong ? "font-medium text-slate-900" : ""}`}>{label}</span>
      <span className={`font-num ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}
